import { createSlice } from '@reduxjs/toolkit';

/**
 * Day Passes state — the Bookings module's Day Passes section. Reads/writes the
 * Azure day-pass admin API through the App Server proxy (/v5/:orgID/daypass/*),
 * so this slice is a thin per-org cache of the several read surfaces (pass
 * types, profiles, capacity, sales log + metrics, sites, devices, org config +
 * branding) plus a single shared mutation lifecycle for the drawers.
 *
 * Modelled on Redux/bookableResources: per-op loading flags, a one-shot
 * `mutationComplete` for drawers to close on, org-keyed data, `auth/logout`
 * reset. Kept generic (one `data[orgID][resource]` map) because every tab
 * renders through the same grid/drawer.
 */

const emptyOrg = () => ({
	org: null, // { organisation, metrics } from GET /org
	passTypes: [],
	capacity: [], // booking_resources
	passes: [],
	passesMetrics: null,
	sites: [],
	devices: [],
	config: null, // { stripe_mode }
	branding: null, // { brandSettings }

	// Day-Pass Fold (platform-native) — pass types read from the new /v5/:orgID/day-pass
	// domain, keyed by bookable resourceID. Distinct from `passTypes` above (Azure proxy)
	// so the fold-migrated wizard and the still-proxy-bound tabs never cross-read.
	foldPassTypes: {}, // foldPassTypes[resourceID] = [...passTypes] (create wizard, per resource)
	foldPassTypesAll: [], // every fold pass type across the org (Pass-types tab), each with resourceName + siteID
	foldSales: [], // every fold sale (Booking with PassTypeID set) for the org (Sales-log tab)
	// Settlement statements (docs/plans/daypass-statements/PLAN.md): the period list, and each
	// period's detail keyed by its period id. Rendered as the API returns them — no fee math here.
	foldStatements: [],
	foldStatementDetail: {} // foldStatementDetail[periodId] = statement ('YYYY-MM-DD', the period's local start)
});

const initialState = {
	data: {}, // data[orgID] = emptyOrg()

	// per-resource read flags
	loading: false, // any list load in flight (coarse spinner)
	passesLoading: false,

	// shared mutation lifecycle (pass-types / capacity / config / branding)
	mutationLoading: false,
	mutationComplete: false,

	// Day-Pass Fold lifecycle — separate flags so the resource-driven wizard's
	// platform writes never entangle with the proxy `mutate` flags above.
	foldPassTypesLoading: false,
	foldPassTypesAllLoading: false,
	foldSalesLoading: false,
	foldStatementsLoading: false,
	foldStatementsError: null, // the list's own failure, rendered by the tab (never shown as "no periods")
	foldStatementLoading: false,
	foldStatementError: null, // { periodId, message } — a per-period failure the detail view shows
	foldRemitLoading: false,
	foldExportLoading: false,
	foldMutationLoading: false,
	foldMutationComplete: false,

	error: null
};

const ensureOrg = (state, orgID) => {
	if (!state.data[orgID]) state.data[orgID] = emptyOrg();
	return state.data[orgID];
};

const dayPassesSlice = createSlice({
	name: 'dayPasses',
	initialState,
	reducers: {
		// 🔥 Generic resource load: loadResource({ resource }) → loadResourceSuccess({ orgID, resource, data })
		loadResource: (state) => {
			state.loading = true;
			state.error = null;
		},
		loadResourceSuccess: (state, action) => {
			state.loading = false;
			const { orgID, resource, data } = action.payload;
			ensureOrg(state, orgID)[resource] = data;
		},
		loadResourceError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
		},

		// 🔥 Sales log (kept separate so its own spinner doesn't blank the whole view)
		loadPasses: (state) => {
			state.passesLoading = true;
			state.error = null;
		},
		loadPassesSuccess: (state, action) => {
			state.passesLoading = false;
			const { orgID, passes, metrics } = action.payload;
			const org = ensureOrg(state, orgID);
			if (passes !== undefined) org.passes = passes;
			if (metrics !== undefined) org.passesMetrics = metrics;
		},
		loadPassesError: (state, action) => {
			state.passesLoading = false;
			state.error = action.payload;
		},

		// 🔥 Shared mutation (create/update/delete a pass type; upsert capacity;
		// update config/branding). The saga refetches the affected resource on
		// success, so reducers only carry the lifecycle flags.
		mutate: (state) => {
			state.mutationLoading = true;
			state.mutationComplete = false;
			state.error = null;
		},
		mutateSuccess: (state) => {
			state.mutationLoading = false;
			state.mutationComplete = true;
		},
		mutateError: (state, action) => {
			state.mutationLoading = false;
			state.error = action.payload;
		},
		resetMutationState: (state) => {
			state.mutationComplete = false;
			state.error = null;
		},

		// 🔥 Day-Pass Fold: load a resource's platform pass types (GET /day-pass/passTypes?resourceID)
		loadFoldPassTypes: (state) => {
			state.foldPassTypesLoading = true;
			state.error = null;
		},
		loadFoldPassTypesSuccess: (state, action) => {
			state.foldPassTypesLoading = false;
			const { orgID, resourceID, data } = action.payload;
			ensureOrg(state, orgID).foldPassTypes[resourceID] = data || [];
		},
		loadFoldPassTypesError: (state, action) => {
			state.foldPassTypesLoading = false;
			state.error = action.payload;
		},

		// 🔥 Day-Pass Fold: load ALL of the org's platform pass types (GET /day-pass/passTypes, no resourceID)
		loadFoldPassTypesAll: (state) => {
			state.foldPassTypesAllLoading = true;
			state.error = null;
		},
		loadFoldPassTypesAllSuccess: (state, action) => {
			state.foldPassTypesAllLoading = false;
			const { orgID, data } = action.payload;
			ensureOrg(state, orgID).foldPassTypesAll = data || [];
		},
		loadFoldPassTypesAllError: (state, action) => {
			state.foldPassTypesAllLoading = false;
			state.error = action.payload;
		},

		// 🔥 Day-Pass Fold: load every sale (GET /day-pass/sales) — the Sales-log tab.
		loadFoldSales: (state) => {
			state.foldSalesLoading = true;
			state.error = null;
		},
		loadFoldSalesSuccess: (state, action) => {
			state.foldSalesLoading = false;
			const { orgID, data } = action.payload;
			ensureOrg(state, orgID).foldSales = data || [];
		},
		loadFoldSalesError: (state, action) => {
			state.foldSalesLoading = false;
			state.error = action.payload;
		},

		// 🔥 Day-Pass Fold: create/update/delete a platform pass type (writes to /day-pass).
		// The saga refetches the affected resource's fold pass types on success.
		foldMutate: (state) => {
			state.foldMutationLoading = true;
			state.foldMutationComplete = false;
			state.error = null;
		},
		foldMutateSuccess: (state) => {
			state.foldMutationLoading = false;
			state.foldMutationComplete = true;
		},
		foldMutateError: (state, action) => {
			state.foldMutationLoading = false;
			state.error = action.payload;
		},
		// 🔥 Settlement statements (GET /day-pass/statements, /statements/:periodId).
		loadFoldStatements: (state) => {
			state.foldStatementsLoading = true;
			state.foldStatementsError = null;
		},
		loadFoldStatementsSuccess: (state, action) => {
			state.foldStatementsLoading = false;
			const { orgID, data } = action.payload;
			ensureOrg(state, orgID).foldStatements = data || [];
		},
		loadFoldStatementsError: (state, action) => {
			state.foldStatementsLoading = false;
			state.foldStatementsError = action.payload;
		},
		loadFoldStatement: (state) => {
			state.foldStatementLoading = true;
			state.foldStatementError = null;
		},
		loadFoldStatementSuccess: (state, action) => {
			state.foldStatementLoading = false;
			const { orgID, periodId, data } = action.payload;
			ensureOrg(state, orgID).foldStatementDetail[periodId] = data;
		},
		loadFoldStatementError: (state, action) => {
			state.foldStatementLoading = false;
			state.foldStatementError = action.payload;
		},
		// Staff only (server-enforced): freeze a period with the bank reference + date.
		remitFoldStatement: (state) => {
			state.foldRemitLoading = true;
			state.error = null;
		},
		remitFoldStatementSuccess: (state, action) => {
			state.foldRemitLoading = false;
			const { orgID, periodId, data } = action.payload;
			// A remit changes what later periods bring forward and whether they are remittable: drop every
			// other cached detail so none is shown stale with a Remit button (round 3, B-15).
			ensureOrg(state, orgID).foldStatementDetail = { [periodId]: data };
		},
		remitFoldStatementError: (state, action) => {
			state.foldRemitLoading = false;
			state.error = action.payload;
		},
		exportFoldStatement: (state) => {
			state.foldExportLoading = true;
		},
		exportFoldStatementDone: (state) => {
			state.foldExportLoading = false;
		},

		resetFoldMutationState: (state) => {
			state.foldMutationComplete = false;
			state.error = null;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const dayPassesActions = dayPassesSlice.actions;
export default dayPassesSlice.reducer;
